import { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { useFormState } from 'react-final-form';

import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { checkHasErrorInFields } from 'functions/checkHasErrorInCollapseBlock';
import { PM_SUBJECT } from 'CorePackage/MainModule/routes/routes';
import {
  COUNTRY_BLOCK_ID,
  DOCUMENT_BLOCK_ID,
  INN_LABEL_BLOCK_ID,
  KPP_BLOCK_ID,
  OGRN_LABEL_BLOCK_ID,
  OKVEDS_BLOCK_ID,
  ORGANIZATION_NAME_BLOCK_ID,
  RAFP_NZA_BLOCK_ID,
  SNILS_BLOCK_ID,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IPmSubjectItemProps, IPmSubjectItemRef, PmSubjectItem } from './PmSubjectItem';
import { usePmErknmSubjectFields } from '../hooks/usePmErknmSubjectFields';
import styles from './PmSubjectItemCollapse.module.scss';

/**
 * Якоря-константы, которые рендерит PmSubjectItem.
 * PM_SUBJECT - якорь секции снаружи аккордеона: карта полей ведёт на него
 * часть ошибок КЛ (например, «Тип контролируемого лица»), и по ним блок
 * тоже должен раскрываться.
 */
const SUBJECT_BLOCK_IDS = [
  PM_SUBJECT,
  ORGANIZATION_NAME_BLOCK_ID,
  COUNTRY_BLOCK_ID,
  DOCUMENT_BLOCK_ID,
  INN_LABEL_BLOCK_ID,
  KPP_BLOCK_ID,
  OGRN_LABEL_BLOCK_ID,
  OKVEDS_BLOCK_ID,
  RAFP_NZA_BLOCK_ID,
  SNILS_BLOCK_ID,
];

interface IPmSubjectItemCollapseProps extends Omit<IPmSubjectItemProps, 'formRef'> {
  remove: () => void;
  isNew: boolean | undefined;
  isConnectedToErvkObject: boolean;
  isLinkedByImpossibility?: boolean;
  isAdminCanEditSubject: boolean;
}

export const PmSubjectItemCollapse = ({
  isNew,
  remove,
  isConnectedToErvkObject,
  inspectionResults,
  isAdminCanEditSubject,
  ...props
}: IPmSubjectItemCollapseProps) => {
  const subjectItemRef = useRef<IPmSubjectItemRef>(null);

  const { getBlockId, name: parentName } = props;
  const subjectFieldNames = usePmErknmSubjectFields({ name: parentName || '', isRemovePrefix: true });

  /**
   * Якоря полей КЛ - несвязанные между собой константы,
   * общего префикса у них нет, поэтому одного id аккордеону не хватит.
   * Перечисляем их, чтобы ссылка из панели ошибок раскрывала блок.
   */
  const revealIds = useMemo(
    () =>
      [...SUBJECT_BLOCK_IDS, subjectFieldNames.subjectTypePm, subjectFieldNames.locationAddress].map(blockId =>
        getBlockId(blockId),
      ),
    [getBlockId, subjectFieldNames.subjectTypePm, subjectFieldNames.locationAddress],
  );

  /**
   * Красная рамка у свёрнутого блока - как в остальных аккордеонах паспорта.
   * checkHasErrorInCollapseBlock здесь не подходит: своего пути в форме у КЛ нет,
   * его поля валидатор спредит в корень (см. getSubjectPmValidate в knmPmValidator),
   * а пустой путь дал бы ошибки всей формы. Поэтому проверяем по списку полей КЛ:
   * ключи берём из того же хука, что использует PmSubjectItem, префикс собираем сами.
   */
  const subjectErrorPaths = useMemo(() => {
    const prefix = parentName ? `${parentName}.` : '';

    return Object.keys(subjectFieldNames)
      .filter(field => field !== 'isOrganizationNew')
      .map(field => `${prefix}${field}`);
  }, [subjectFieldNames, parentName]);

  const { errors } = useFormState({ subscription: { errors: true } });

  const hasError = useMemo(() => checkHasErrorInFields(errors, subjectErrorPaths), [errors, subjectErrorPaths]);

  // KnmCollapse отдаёт в changeOpenedCallback именно свёрнутость, в том числе при монтировании
  const [isRolledUp, setIsRolledUp] = useState<boolean>(!isNew);

  const isRemoveAvailable =
    !isConnectedToErvkObject && (!props.watchOnly || !!props.isLinkedByImpossibility || isAdminCanEditSubject);

  const handleClose = useCallback(() => {
    subjectItemRef.current?.onClear();
    remove();
  }, [remove]);

  const inspectionResultAvailable = inspectionResults?.find(
    i =>
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_NAME ||
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NOT_FOUND ||
      i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_ADDRESS,
  );

  /**
   * Генерация имени гармошки объекта
   */
  const generateCollapseName = useMemo(() => {
    if (inspectionResultAvailable) {
      return <ErknmInspectionResultMessage inspectionResult={inspectionResultAvailable} />;
    }

    return undefined;
  }, [inspectionResultAvailable]);

  return (
    <KnmCollapse
      revealIds={revealIds}
      error={isRolledUp && hasError}
      changeOpenedCallback={setIsRolledUp}
      title="Контролируемое лицо"
      subtitle={generateCollapseName}
      isNew={isNew}
      onClose={isRemoveAvailable ? handleClose : undefined}
      openByDefault={isNew}
      headerClassName={cn([
        {
          [styles.BorderYellow]:
            inspectionResultAvailable &&
            inspectionResultAvailable.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
          [styles.BorderRed]:
            inspectionResultAvailable &&
            inspectionResultAvailable.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
        },
      ])}
    >
      <PmSubjectItem ref={subjectItemRef} {...props} inspectionResults={inspectionResults} />
    </KnmCollapse>
  );
};
